import React, { useState } from 'react';
import { AgentSignal } from '../../../types/monitor.types';
import { formatTimeUTC6, formatDateTimeUTC6, formatFullDateTimeUTC6 } from '../../../utils/formatters';

interface Props {
  signals: AgentSignal[];
  loading: boolean;
  onRefresh: () => void;
  showDate?: boolean;  // Historical page shows MM/DD HH:MM; live monitor only HH:MM
}

const actionClass = (action: string, placed: boolean, reviewerVerdict: string | null): string => {
  if (action === 'PASS') return 'agent-action-pass';
  if (reviewerVerdict === 'BLOCK') return 'agent-action-blocked';
  if (placed) return 'agent-action-placed';
  return 'agent-action-proposed';
};

const outcomeClass = (outcome: string | null): string => {
  if (!outcome) return 'outcome-pending';
  if (outcome === 'WIN') return 'outcome-win';
  if (outcome === 'LOSS') return 'outcome-loss';
  if (outcome === 'BREAKEVEN') return 'outcome-be';
  if (outcome === 'BLOCKED') return 'outcome-blocked';
  if (outcome === 'NO_TRADE') return 'outcome-notrade';
  return 'outcome-other';
};

const AgentSignals: React.FC<Props> = ({ signals, loading, onRefresh, showDate = false }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggle = (id: string) => setExpandedId(prev => (prev === id ? null : id));

  if (loading && signals.length === 0) {
    return (
      <div className="agent-signals-section">
        <p className="agent-signals-empty">Loading agent signals...</p>
      </div>
    );
  }

  if (signals.length === 0) {
    return (
      <div className="agent-signals-section">
        <div className="agent-signals-header">
          <button className="reviews-refresh-btn" onClick={onRefresh}>↻ Refresh</button>
        </div>
        <p className="agent-signals-empty">No discovery ticks yet (fires hourly 04:00–11:00 UTC-6 across NATURALGAS, OIL_CRUDE, GOLD, SILVER, US100, US500)</p>
      </div>
    );
  }

  const tradeCount = signals.filter(s => s.action === 'TRADE').length;
  const placedCount = signals.filter(s => s.placed).length;

  return (
    <div className="agent-signals-section">
      <div className="agent-signals-header">
        <span className="agent-signals-stats">
          <span className="agent-stat-total">{signals.length} ticks</span>
          <span className="agent-stat-divider">·</span>
          <span className="agent-stat-trade">{tradeCount} TRADE</span>
          <span className="agent-stat-divider">·</span>
          <span className="agent-stat-placed">{placedCount} placed</span>
        </span>
        <button className="reviews-refresh-btn" onClick={onRefresh}>↻ Refresh</button>
      </div>
      <div className="agent-signals-table-wrap">
        <table className="agent-signals-table">
          <thead>
            <tr>
              <th></th>
              <th>Time</th>
              <th>Instrument</th>
              <th>Setup</th>
              <th>Dir</th>
              <th>Action</th>
              <th className="hide-mobile">Conf</th>
              <th className="hide-mobile">Entry</th>
              <th className="hide-mobile">Stop</th>
              <th className="hide-mobile">Target</th>
              <th>Reviewer</th>
              <th>Outcome</th>
              <th className="hide-mobile">P/L</th>
            </tr>
          </thead>
          <tbody>
            {signals.map(s => {
              const expanded = expandedId === s.signalId;
              return (
                <React.Fragment key={s.signalId}>
                  <tr
                    className={`review-row ${expanded ? 'review-row-expanded' : ''}`}
                    onClick={() => toggle(s.signalId)}
                  >
                    <td className="td-chevron">{expanded ? '▾' : '▸'}</td>
                    <td className="td-time">{showDate ? formatDateTimeUTC6(s.generatedAt) : formatTimeUTC6(s.generatedAt)}</td>
                    <td className="td-mono td-instrument">{s.instrument}</td>
                    <td className="td-mono">{s.setupType ?? '—'}</td>
                    <td>
                      {s.direction
                        ? <span className={`dir-tag ${s.direction === 'BUY' ? 'dir-buy' : 'dir-sell'}`}>{s.direction}</span>
                        : <span className="dim">—</span>}
                    </td>
                    <td>
                      <span className={`agent-action-badge ${actionClass(s.action, s.placed, s.reviewerVerdict)}`}>
                        {s.action === 'PASS' ? 'PASS' : (s.placed ? 'PLACED' : (s.reviewerVerdict === 'BLOCK' ? 'BLOCKED' : 'PROPOSED'))}
                      </span>
                    </td>
                    <td className="td-mono hide-mobile">
                      {s.confidence != null ? `${Math.round(s.confidence * 100)}%` : '—'}
                    </td>
                    <td className="td-mono hide-mobile">{s.entryPrice != null ? s.entryPrice.toFixed(4) : '—'}</td>
                    <td className="td-mono hide-mobile">{s.stopPrice != null ? s.stopPrice.toFixed(4) : '—'}</td>
                    <td className="td-mono hide-mobile">{s.targetPrice != null ? s.targetPrice.toFixed(4) : '—'}</td>
                    <td>
                      {s.reviewerVerdict
                        ? <span className={`verdict-badge ${s.reviewerVerdict === 'ALLOW' ? 'verdict-allow' : 'verdict-block'}`}>
                            {s.reviewerVerdict}
                          </span>
                        : <span className="dim">—</span>}
                    </td>
                    <td>
                      <span className={`outcome-badge ${outcomeClass(s.tradeOutcome)}`}>
                        {s.tradeOutcome ?? 'PENDING'}
                      </span>
                    </td>
                    <td className={`td-mono td-pl hide-mobile ${s.pnlUsd != null && s.pnlUsd < 0 ? 'pl-neg' : s.pnlUsd != null && s.pnlUsd > 0 ? 'pl-pos' : ''}`}>
                      {s.pnlUsd != null ? `$${s.pnlUsd.toFixed(2)}` : '—'}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="review-detail-row">
                      <td colSpan={13}>
                        <div className="review-detail">
                          {s.reasoning && (
                            <div className="review-section">
                              <div className="review-section-title">Agent reasoning</div>
                              <div className="review-reasoning">{s.reasoning}</div>
                            </div>
                          )}
                          {s.reviewerReasoning && (
                            <div className="review-section">
                              <div className="review-section-title">Reviewer reasoning</div>
                              <div className="review-reasoning">{s.reviewerReasoning}</div>
                            </div>
                          )}
                          {s.placementError && (
                            <div className="review-section">
                              <div className="review-section-title review-section-red">Placement error</div>
                              <div className="review-reasoning">{s.placementError}</div>
                            </div>
                          )}
                          <div className="review-meta">
                            <span><b>signal_id:</b> {s.signalId}</span>
                            <span><b>timeframe:</b> {s.timeframe}</span>
                            <span><b>candle_close:</b> {formatFullDateTimeUTC6(s.candleCloseAt)}</span>
                            {s.tradeId != null && <span><b>trade_id:</b> {s.tradeId}</span>}
                            <span><b>tools:</b> {s.toolsUsed ?? 0}</span>
                            <span><b>latency:</b> {s.latencyMs != null ? `${(s.latencyMs / 1000).toFixed(1)}s` : '—'}</span>
                            {s.failOpen && <span className="review-failopen-tag">synthetic fallback (agent fail-closed)</span>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgentSignals;
